import React, { useContext } from "react";
import PropTypes from "prop-types";
import EMPDataCard from "../../shared/components/EMPDataCard/EMPDataCard";
import { Grid, Box, Typography } from "@mui/material";
import { formatToSimpleDate, capitalizeText } from "../../utils/EmpUtil";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import EMPBanner from "../../shared/components/EMPBanner/EMPBanner";
import { ModalContext } from "../../shared/components/EMPModal/ModalContext";
import TradeInDecision from "../../shared/components/EMPModal/ModalFragments/TradeInDecision";
import { useTranslation } from "react-i18next";
import { TRADE_IN_OWNERSHIP_TYPES } from "../../shared/constants/TradeInConstants";
import LeasedTradeInTable from "./tradein-tables/LeasedTradeInTable";
import OwnedTradeInTable from "./tradein-tables/OwnedTradeInTable";
import FinancedTradeInTable from "./tradein-tables/FinancedTradeInTable";
import { UserContext } from "../../shared/context/userContext";
import LincolnTradeIn, { getTradeInStatusText } from "./LincolnTradeIn";

const extractAppraisalData = (content, t) => {
  const { tradeIn } = content;

  if (tradeIn) {
    const appraisalData = {
      appraisalId: tradeIn.appraisalId,
      appraisalVehicle: `${tradeIn.modelYear} ${tradeIn.vehicleMake} ${
        tradeIn.vehicleModel
      } ${tradeIn.trim || ""}`,
      netEquity:
        tradeIn.netEquity ?? content.customerReservation?.tradeIn?.netEquity,
      amounts: [],
    };

    if (tradeIn.ownershipType === "LEASED") {
      const leasedItems = [
        {
          heading: t("tradeIn.quoted"),
          amount: Number(tradeIn.totalOutstandingBalanceAmount),
          tooltip: {
            body: t(
              "OrderDetailsConsumerDeposit.tradeInObligationQuotedTooltipBody"
            ),
          },
        },
        {
          heading: t("tradeIn.enteredByCustomer"),
          amount: tradeIn?.enteredObligation
            ? Number(tradeIn.enteredObligation)
            : 0,
          tooltip: {
            body: t(
              "OrderDetailsConsumerDeposit.tradeInObligationEnteredTooltipBody"
            ),
          },
        },
      ];

      appraisalData.amounts.push(...leasedItems);
    }

    if (tradeIn.ownershipType === "FINANCED") {
      const financedItems = [
        {
          id: "appraisal",
          heading: "Appraisal Amount",
          amount: Number(tradeIn.appraisalAmount),
          tooltip: {
            body: t(
              "OrderDetailsConsumerDeposit.tradeInAppraisalAmountTooltipBody"
            ),
          },
        },
      ];

      if (tradeIn.enteredBalance) {
        financedItems.push({
          id: "entered",
          heading: t("TradeIn.US.enteredBalance"),
          amount: -Number(tradeIn.enteredBalance),
          tooltip: {
            body: t(
              "OrderDetailsConsumerDeposit.tradeInRemainingBalanceEnteredTooltipBody"
            ),
          },
        });
      } else {
        financedItems.push({
          id: "remaining",
          heading: t("TradeIn.US.remainingBalance"),
          amount: -Number(tradeIn.remainingBalance),
          tooltip: {
            body: t(
              "OrderDetailsConsumerDeposit.tradeInRemainingBalanceQuotedTooltipBody"
            ),
          },
        });
      }

      appraisalData.amounts.push(...financedItems);
    }

    if (tradeIn.ownershipType === "OWNED") {
      const ownedItems = [
        {
          heading: "Appraisal Amount",
          amount: Number(tradeIn.appraisalAmount),
          tooltip: {
            body: t(
              "OrderDetailsConsumerDeposit.tradeInAppraisalAmountTooltipBody"
            ),
          },
        },
      ];

      appraisalData.amounts.push(...ownedItems);
    }

    return appraisalData;
  }
};

const extractTradeInData = (content, t, isTradeInLease = false) => {
  const { tradeIn } = content;

  if (!tradeIn) return tradeIn;

  const data = [];

  if (tradeIn.status !== null) {
    data.push({
      heading: t("tradeIn.fields.0"),
      content: getTradeInStatusText(tradeIn.status, t),
      highlight: true,
      color: "success",
    });
  }

  if (tradeIn.rejectedReason) {
    data.push({
      heading: t("tradeIn.fields.1"),
      content: tradeIn.rejectedReason,
    });
  }

  if (tradeIn.acceptedDateNA) {
    data.push({
      heading: t("tradeIn.fields.2"),
      content: formatToSimpleDate(tradeIn.acceptedDateNA),
    });
  }

  if (tradeIn.userId) {
    data.push({
      heading: t("tradeIn.fields.3"),
      content: tradeIn.userId,
    });
  }

  data.push({
    heading: t("tradeIn.fields.4"),
    content: capitalizeText(tradeIn.ownershipType),
  });

  data.push({ heading: t("tradeIn.fields.5"), content: tradeIn.vin });

  data.push({
    heading: t("tradeIn.fields.6"),
    content: tradeIn.expiryDateTimestamp
      ? formatToSimpleDate(tradeIn.expiryDateTimestamp)
      : "--",
  });

  data.push({ heading: t("tradeIn.fields.7"), content: tradeIn.modelYear });

  if (!isTradeInLease) {
    data.push({
      heading: t("tradeIn.fields.8"),
      content: tradeIn.mileage ? `${tradeIn.mileage} miles` : "--",
    });

    data.push({
      heading: t("tradeIn.fields.9"),
      content: tradeIn.conditionRating
        ? Number(tradeIn.conditionRating).toFixed(1)
        : "--",
    });
  }

  return data;
};

const extractConditionData = (content, t) => {
  const { tradeIn } = content;

  return (
    tradeIn && [
      {
        heading: t("tradeIn.fields.10"),
        content: tradeIn.overallCondition || "--",
      },
      {
        heading: t("tradeIn.fields.11"),
        content: tradeIn.hasOdors === true ? "Yes" : "No",
      },
      {
        heading: t("tradeIn.fields.12"),
        content: tradeIn.hasMajorDamage === true ? "Yes" : "No",
      },
      {
        heading: t("tradeIn.fields.13"),
        content: tradeIn.hasMinorDamage === true ? "Yes" : "No",
      },
      {
        heading: t("tradeIn.fields.14"),
        content: tradeIn.hasIlluminatedWarningLights === true ? "Yes" : "No",
      },
      {
        heading: t("tradeIn.fields.15"),
        content: tradeIn.hasAccidents === true ? "Yes" : "No",
      },
    ]
  );
};

const TradeIn = ({ content, orderId }) => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const { handleModal } = useContext(ModalContext);
  if (userContext?.user?.brand === "L") {
    return (
      <LincolnTradeIn
        content={content}
        t={t}
        onStartDecision={() =>
          handleModal(
            t("tradeIn.confirmTradeInDetails"),
            <TradeInDecision orderId={orderId} />
          )
        }
      />
    );
  }
  const isTradeInLease =
    content?.tradeIn?.ownershipType === TRADE_IN_OWNERSHIP_TYPES.LEASED;
  const dealerOptIn = content?.customerReservation?.dealerTradeInOptIn;
  const appraisalDetails = extractAppraisalData(content, t);
  const tradeInVehicleDetails = extractTradeInData(content, t, isTradeInLease);
  const tradeInConditionDetails = extractConditionData(content, t);
  return content.tradeIn !== null ? (
    <Grid container spacing={6}>
      <Grid item xs={12}>
        <EMPBanner
          title={t("common.banners.estimatedTradeIn.title")}
          content={t("common.banners.estimatedTradeIn.content")}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={4} lg={4}>
        {content.tradeIn.ownershipType.toUpperCase() ===
          TRADE_IN_OWNERSHIP_TYPES.LEASED && (
          <LeasedTradeInTable t={t} appraisalDetails={appraisalDetails} />
        )}
        {content.tradeIn.ownershipType.toUpperCase() ===
          TRADE_IN_OWNERSHIP_TYPES.OWNED && (
          <OwnedTradeInTable t={t} appraisalDetails={appraisalDetails} />
        )}
        {content.tradeIn.ownershipType.toUpperCase() ===
          TRADE_IN_OWNERSHIP_TYPES.FINANCED && (
          <FinancedTradeInTable t={t} appraisalDetails={appraisalDetails} />
        )}
      </Grid>

      <Grid item xs={12} sm={12} md={8} lg={8}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          columnGap={4}
          rowGap={4}
          sx={{ mb: 11 }}
        >
          <Box
            gridColumn={{
              xs: "span 12",
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h3" sx={{ color: "text.primary" }}>
                  {t("tradeIn.tradeIn", { context: "vehicleInfo" })}
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {t(
                    "OrderDetailsConsumerDeposit.tradeInConditionDetailsTooltipBody"
                  )}
                </Typography>
              </Grid>
              <Grid item>
                {tradeInVehicleDetails[0].content === "evaluate" &&
                  dealerOptIn && (
                    <EMPButton
                      onClick={() =>
                        handleModal(
                          t("tradeIn.confirmTradeInDetails"),
                          <TradeInDecision orderId={orderId} />
                        )
                      }
                    >
                      {t("common.buttonCtas.startTradeInDecision")}
                    </EMPButton>
                  )}
              </Grid>
            </Grid>
          </Box>
          <Box
            gridColumn={{
              xs: "span 12",
            }}
          >
            <EMPDataCard data={tradeInVehicleDetails} twoByTwo />
          </Box>
          {!isTradeInLease && (
            <>
              <Box
                gridColumn={{
                  xs: "span 12",
                }}
                sx={{ mt: 2 }}
              >
                <Typography variant="h3" sx={{ color: "text.primary" }}>
                  {t("tradeIn.tradeIn", { context: "condition" })}
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {t(
                    "OrderDetailsConsumerDeposit.tradeInConditionDetailsTooltipBody"
                  )}
                </Typography>
              </Box>
              <Box
                gridColumn={{
                  xs: "span 12",
                }}
              >
                <EMPDataCard data={tradeInConditionDetails} twoByTwo />
              </Box>
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  ) : (
    <>
      <Typography variant="h3" sx={{ color: "text.primary", mb: 7 }}>
        {t("tradeIn.tradeIn")}
      </Typography>
      <Typography variant="h5" component="p">
        {t("tradeIn.noTradeInFound")}
      </Typography>
    </>
  );
};

TradeIn.propTypes = {
  orderId: PropTypes.string.isRequired,
  content: PropTypes.shape({
    tradeIn: PropTypes.oneOfType([
      PropTypes.oneOf([null]),
      PropTypes.shape({
        ownershipType: PropTypes.string,
        status: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.oneOf([null]),
        ]),
        appraisalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        appraisalAmount: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        enteredBalance: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        remainingBalance: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.oneOf([null]),
        ]),
        modelYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        vehicleMake: PropTypes.string,
        vehicleModel: PropTypes.string,
        trim: PropTypes.string,
        acceptedDateNA: PropTypes.string,
        userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        rejectedReason: PropTypes.string,
        vin: PropTypes.string,
        expiryDateTimestamp: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        mileage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        conditionRating: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        overallCondition: PropTypes.string,
        hasOdors: PropTypes.bool,
        hasMajorDamage: PropTypes.bool,
        hasMinorDamage: PropTypes.bool,
        hasIlluminatedWarningLights: PropTypes.bool,
        hasAccidents: PropTypes.bool,
        hasMaintenanceHistory: PropTypes.bool,
        tradeInExpired: PropTypes.bool,
        totalOutstandingBalanceAmount: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        enteredObligation: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        netEquity: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.oneOf([null]),
        ]),
      }),
    ]),
    customerReservation: PropTypes.shape({
      dealerTradeInOptIn: PropTypes.bool,
      paymentType: PropTypes.string,
      subJourneyType: PropTypes.string,
      purchaseInfo: PropTypes.shape({
        acceptRejectStatus: PropTypes.string,
        purchaseType: PropTypes.string,
        purchaseRequestDate: PropTypes.string,
        soldDate: PropTypes.string,
        stockId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        purchaseRequestDealerAction: PropTypes.shape({
          dealerAcceptedDate: PropTypes.string,
          dealerActionUserId: PropTypes.string,
          dealerRejectedReason: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
};

export default TradeIn;
